import React from 'react';
import { Box, Typography, Grid, Card, CardContent, Alert } from '@mui/material';
import { CheckCircle, FolderOpen, Key, Settings } from 'lucide-react';

const LoginStep = () => {
    return (
        <>
            <Typography variant="h5" fontWeight={700} gutterBottom sx={{ fontSize: '2.25rem', lineHeight: '2.5rem' }}>Step 1: Login</Typography>
            <Typography variant="body1" color="#6b7280" gutterBottom sx={{ mb: 6, fontSize: '1.25rem', lineHeight: '1.75rem' }}>
                Sign in using your GitHub, GitLab, Bitbucket or Azure DevOps account for secure authentication and easy onboarding.
            </Typography>

            {/* Two Column Layout for Login Processes */}
            <Grid container spacing={6} sx={{ mb: 4 }}>
                {/* New Users Column */}
                <Grid item xs={12} md={6}>
                    <Box sx={{
                        bgcolor: '#fff',
                        border: '0 solid #e2e8f0',
                        height: '100%',
                        p: 6,
                        borderRadius: '12px'
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                            <Box sx={{
                                width: { xs: 22, sm: 30 },
                                height: { xs: 22, sm: 30 },
                                borderRadius: '50%',
                                bgcolor: '#dcfce7',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}>
                                <CheckCircle color='#10b981' size={18} />
                            </Box>
                            <Typography variant="h6" fontWeight={600} sx={{ color: '#1f1f1f' }}>
                                New Users - Sign Up Process
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                            {[
                                "Click the \"Login with GitHub\", \"Login with GitLab\", \"Login with Bitbucket\" or \"Login with Azure DevOps\" button on the homepage",
                                "You'll be redirected to the respective platform's authorization page",
                                "Grant access to your account (we only request read access to public repos)",
                                "You'll be redirected back to our application",
                                "Your account will be automatically created and you'll be taken to the Plans page"
                            ].map((step, index) => (
                                <Box key={step} sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                                    <Box sx={{
                                        width: 20,
                                        height: 20,
                                        borderRadius: '50%',
                                        bgcolor: '#3b82f6',
                                        color: 'white',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: '12px',
                                        fontWeight: 600,
                                        flexShrink: 0,
                                        mt: 0.5
                                    }}>
                                        {index + 1}
                                    </Box>
                                    <Typography variant="body1" color="#1f1f1f" 
                                    sx={{ 
                                        lineHeight: '1.25rem',
                                        fontSize: '0.875rem',
                                    }}>
                                        {step}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                    </Box>
                </Grid>

                {/* Existing Users Column */}
                <Grid item xs={12} md={6}>
                    <Box sx={{
                        bgcolor: '#fff',
                        border: '0 solid #e2e8f0',
                        height: '100%',
                        p: 6,
                        borderRadius: '12px'
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                            <Box sx={{
                                width: { xs: 22, sm: 30 },
                                height: { xs: 22, sm: 30 },
                                borderRadius: '50%',
                                bgcolor: '#dbeafe',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}>
                                <Key color='#3b82f6' size={18} />
                            </Box>
                            <Typography variant="h6" fontWeight={600} sx={{ color: '#1f1f1f' }}>
                                Existing Users - Login Process
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                            {[
                                "Click the \"Login with GitHub\", \"Login with GitLab\", \"Login with Bitbucket\" or \"Login with Azure DevOps\" button on the homepage",
                                "You'll be redirected to the respective platform's authorization page",
                                "Grant access to your account",
                                "You'll be redirected back to our application",
                                "You'll be taken directly to your Projects dashboard"
                            ].map((step, index) => (
                                <Box key={index} sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                                    <Box sx={{
                                        width: 20,
                                        height: 20,
                                        borderRadius: '50%',
                                        bgcolor: '#3b82f6',
                                        color: 'white',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: '12px',
                                        fontWeight: 600,
                                        flexShrink: 0,
                                        mt: 0.5
                                    }}>
                                        {index + 1}
                                    </Box>
                                    <Typography variant="body1" color="#1f1f1f" 
                                    sx={{ 
                                        lineHeight: '1.25rem',
                                        fontSize: '0.875rem',
                                    }}>
                                        {step}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                    </Box>
                </Grid>
            </Grid>

            {/* Information Boxes */}
            <Box sx={{ mb: 4, display: 'flex', flexDirection: 'column', gap: 3, width: '100%' }}>
                <Alert
                    severity="info"
                    sx={{
                        bgcolor: '#ebf2fe',
                        color: '#000',
                        border: 'none',
                        borderRadius: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-start',
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" fontWeight={600}>
                            {'Privacy & Security'}
                        </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                        {'We only request read access to your repositories. We never store your passwords or have write access to your code.'}
                    </Typography>
                </Alert>
                <Alert
                    severity="success"
                    sx={{
                        bgcolor: '#21c45d1a',
                        color: '#000',
                        border: 'none',
                        borderRadius: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-start',
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" fontWeight={600}>
                            {'First Time Users'}
                        </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                        {'Your account is automatically created during the first login. No separate signup form is needed!'}
                    </Typography>
                </Alert>
            </Box>

            {/* After Successful Login Section */}
            <Box sx={{
                bgcolor: 'white',
                border: '0 solid #e2e8f0',
                p: 4,
                borderRadius: '12px',
            }}>
                <Typography variant="h5" fontWeight={700} sx={{ textAlign: 'start', mb: 1, ml: 3, color: '#1f1f1f' }}>
                    After Successful Login
                </Typography>

                <Grid container spacing={3}>
                    {/* New Users Section */}
                    <Grid item xs={12} sm={6}>
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            textAlign: 'start',
                            p: { xs: 2, sm: 3 },
                            borderRadius: 2,
                            border: '0 solid #e2e8f0',
                            height: '100%',
                            minHeight: { xs: 120, sm: 140 },
                            gap: 2
                        }}>
                            <Box sx={{
                                width: { xs: 32, sm: 40 },
                                height: { xs: 32, sm: 40 },
                                borderRadius: '50%',
                                bgcolor: '#dbeafe',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}>
                                <Settings color='#3b82f6' size={20} />
                            </Box>
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', justifyContent: 'center', }}>
                                <Typography variant="h6" fontWeight={600} sx={{ color: '#1f1f1f', mb: 1, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                    New Users
                                </Typography>
                                <Typography variant="body2" color="#737373" sx={{ lineHeight: 1.5, fontSize: { xs: '0.875rem', sm: '0.9rem' } }}>
                                    Plans page to choose your subscription
                                </Typography>
                            </Box>
                        </Box>
                    </Grid>

                    {/* Existing Users Section */}
                    <Grid item xs={12} sm={6}>
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            textAlign: 'start',
                            p: { xs: 2, sm: 3 },
                            borderRadius: 2,
                            border: '0 solid #bbf7d0',
                            height: '100%',
                            minHeight: { xs: 120, sm: 140 },
                            gap: 2
                        }}>
                            <Box sx={{
                                width: { xs: 32, sm: 40 },
                                height: { xs: 32, sm: 40 },
                                borderRadius: '50%',
                                bgcolor: '#dcfce7',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}>
                                <FolderOpen color='#10b981' size={20} />
                            </Box>
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', justifyContent: 'center', }}>
                                <Typography variant="h6" fontWeight={600} sx={{ color: '#1f1f1f', mb: 1, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                    Existing Users
                                </Typography>
                                <Typography variant="body2" color="#737373" sx={{ lineHeight: 1.5, fontSize: { xs: '0.875rem', sm: '0.9rem' } }}>
                                    Projects dashboard to manage your projects
                                </Typography>
                            </Box>
                        </Box>
                    </Grid>
                </Grid>
            </Box>
        </>
    );
};

export default LoginStep;